/**
 * Route table for the secondary (side) pane.
 *
 * Maps URL paths to page components. Used by SecondaryPane to resolve
 * a tab's stored path into a renderable component via matchPath().
 *
 * This mirrors the routes in AppRouter.tsx. When a new page is added to
 * the app, it should be added here too if it should be openable in the
 * side pane. Pages that don't appear here will show a "not available"
 * message in the side pane.
 *
 * Layout types match the two layout wrappers in AppRouter:
 * - 'full-bleed': fills the pane, no padding, scrolls if the page overflows
 * - 'constrained': left-aligned max-width container, scrolls
 */
import React from 'react';

import { Navigate } from 'react-router-dom';

import {
  AgendaPage,
  AllAreasPage,
  BlockedDaysPage,
  BudgetCategoryPage,
  BudgetPage,
  BulletinBoardInboxPage,
  SnoozedInboxPage,
  CashFlowPage,
  Chat,
  ContactsPage,
  Conversations,
  DebtSavingsPage,
  EmailLabelsPage,
  EmailPage,
  EmailSettingsPage,
  FoodDiaryFoodsPage,
  FoodDiaryLayout,
  FoodEditorPage,
  FoodsPage,
  HistoryInboxPage,
  HomePage,
  HouseholdPage,
  IdeaFactoryPage,
  WritingBoardPage,
  InboxPage,
  InvestmentsPage,
  JobsTab,
  JournalPage,
  LaterInboxPage,
  MealsLayout,
  MealsSettingsPage,
  MessagesHubPage,
  NutritionPage,
  OverviewPage,
  Quotes,
  ReportsPage,
  ReportView,
  ResearchPage,
  RetirementPage,
  SearchPage,
  SettingsPage,
  SourceEditorPage,
  SourceReaderPage,
  SpamPage,
  SpendingAnalysisPage,
  SpendingByTagPage,
  StudyPage,
  TasksPage,
  ThisWeekPage,
  TransactionsPage,
  TripMenuDetailPage,
  TripMenusPage,
  WellnessDashboard,
  WorkflowEditorPage,
} from '@app/routes/lazyPages';
import { APP_INSTALL_REDIRECTS } from '@shared/app-identity/appHomePaths';
import { ATTENTION_ROUTES } from '@shared/constants/routes';

import type { RouteLayout } from '@shared/components/layout';

/**
 * Aliased rather than redeclared: `RouteSurface` owns the shapes, and both
 * panes render through it, so a new shape cannot be added on one side only.
 */
export type AppRouteLayout = RouteLayout;

export interface AppRoute {
  path: string;
  element: React.ReactElement;
  layout: AppRouteLayout;
}

/**
 * All routes available in the secondary pane. Order matters — first
 * match wins (same as React Router's matching behavior).
 */
export const APP_ROUTES: AppRoute[] = [
  // Full-bleed routes
  { path: ATTENTION_ROUTES.mailAll, element: <EmailPage viewMode="all" />, layout: 'full-bleed' },
  {
    path: ATTENTION_ROUTES.starred,
    element: <EmailPage viewMode="starred" />,
    layout: 'full-bleed',
  },
  { path: ATTENTION_ROUTES.mailSent, element: <EmailPage viewMode="sent" />, layout: 'full-bleed' },
  {
    path: ATTENTION_ROUTES.mailDrafts,
    element: <EmailPage viewMode="drafts" />,
    layout: 'full-bleed',
  },
  { path: ATTENTION_ROUTES.mailLabels, element: <EmailLabelsPage />, layout: 'full-bleed' },
  { path: ATTENTION_ROUTES.mailSpam, element: <SpamPage />, layout: 'full-bleed' },
  { path: ATTENTION_ROUTES.mailSettings, element: <EmailSettingsPage />, layout: 'constrained' },
  { path: '/home', element: <HomePage />, layout: 'full-bleed' },
  { path: ATTENTION_ROUTES.root, element: <InboxPage />, layout: 'full-bleed' },
  { path: ATTENTION_ROUTES.message, element: <InboxPage />, layout: 'full-bleed' },
  { path: ATTENTION_ROUTES.history, element: <HistoryInboxPage />, layout: 'full-bleed' },
  { path: ATTENTION_ROUTES.readingRoom, element: <LaterInboxPage />, layout: 'full-bleed' },
  {
    path: ATTENTION_ROUTES.bulletinBoard,
    element: <BulletinBoardInboxPage />,
    layout: 'full-bleed',
  },
  { path: ATTENTION_ROUTES.snoozed, element: <SnoozedInboxPage />, layout: 'full-bleed' },
  { path: '/journal', element: <JournalPage />, layout: 'full-bleed' },
  { path: '/journal/:date', element: <JournalPage />, layout: 'full-bleed' },
  // '/notes' is still produced (the AI nav intent and the destination's
  // activeBasePath); the Notes surface itself lives at '/notes/editor'.
  { path: '/notes', element: <Navigate to="/notes/editor" replace />, layout: 'full-bleed' },
  { path: '/notes/editor', element: <SourceEditorPage />, layout: 'full-bleed' },
  { path: '/study/channels', element: <StudyPage />, layout: 'full-bleed' },
  // Must precede '/study/:channelId': SecondaryPane takes the first matchPath
  // hit in table order, so a dynamic segment above this would swallow 'ideas'.
  { path: '/study/ideas', element: <IdeaFactoryPage />, layout: 'full-bleed' },
  // The writing board, its own Study feature. Static 'writing' must also
  // precede '/study/:channelId' for the same reason as 'ideas' above.
  {
    path: '/study/writing/:boardId/edit/:itemId',
    element: <WritingBoardPage />,
    layout: 'full-bleed',
  },
  {
    path: '/study/writing/:boardId/library',
    element: <WritingBoardPage view="library" />,
    layout: 'full-bleed',
  },
  { path: '/study/writing/:boardId', element: <WritingBoardPage />, layout: 'full-bleed' },
  { path: '/study/:channelId/:view/edit/:itemId', element: <StudyPage />, layout: 'full-bleed' },
  { path: '/study/:channelId/:view', element: <StudyPage />, layout: 'full-bleed' },
  { path: '/study/:channelId', element: <StudyPage />, layout: 'full-bleed' },
  { path: '/study', element: <StudyPage />, layout: 'full-bleed' },
  { path: '/chat', element: <Chat />, layout: 'full-bleed' },
  { path: '/messages', element: <MessagesHubPage />, layout: 'full-bleed' },
  { path: '/tasks', element: <TasksPage />, layout: 'full-bleed' },
  { path: '/my-data/contacts', element: <ContactsPage />, layout: 'full-bleed' },
  {
    path: '/meals/foods/new',
    element: (
      <MealsLayout section="foods">
        <FoodEditorPage />
      </MealsLayout>
    ),
    layout: 'full-bleed',
  },
  {
    path: '/meals/foods/:id/edit',
    element: (
      <MealsLayout section="foods">
        <FoodEditorPage />
      </MealsLayout>
    ),
    layout: 'full-bleed',
  },
  {
    path: '/meals/foods',
    element: (
      <MealsLayout section="foods">
        <FoodsPage />
      </MealsLayout>
    ),
    layout: 'full-bleed',
  },
  {
    path: '/meals/settings',
    element: (
      <MealsLayout section="settings">
        <MealsSettingsPage />
      </MealsLayout>
    ),
    layout: 'full-bleed',
  },
  {
    path: '/meals/trips',
    element: (
      <MealsLayout section="trips">
        <TripMenusPage />
      </MealsLayout>
    ),
    layout: 'full-bleed',
  },
  {
    path: '/meals/trips/:id',
    element: (
      <MealsLayout section="trips">
        <TripMenuDetailPage />
      </MealsLayout>
    ),
    layout: 'full-bleed',
  },
  {
    path: '/meals/blocked-days',
    element: (
      <MealsLayout section="blocked-days">
        <BlockedDaysPage />
      </MealsLayout>
    ),
    layout: 'full-bleed',
  },
  {
    path: '/food-diary',
    element: (
      <FoodDiaryLayout>
        <NutritionPage />
      </FoodDiaryLayout>
    ),
    layout: 'full-bleed',
  },
  {
    path: '/food-diary/foods',
    element: (
      <FoodDiaryLayout>
        <FoodDiaryFoodsPage />
      </FoodDiaryLayout>
    ),
    layout: 'full-bleed',
  },
  {
    path: '/meals',
    element: (
      <MealsLayout section="meals">
        <ThisWeekPage />
      </MealsLayout>
    ),
    layout: 'full-bleed',
  },

  // Settings. The workflow editor precedes `/settings/:section/:item`: this table
  // is matched in order, and the param route would otherwise swallow it.
  {
    path: '/settings/assistant/capabilities/workflows/new',
    element: <WorkflowEditorPage />,
    layout: 'full-bleed',
  },
  {
    path: '/settings/assistant/capabilities/workflows/:id/edit',
    element: <WorkflowEditorPage />,
    layout: 'full-bleed',
  },
  { path: '/settings/:section/:item', element: <SettingsPage />, layout: 'full-bleed' },
  { path: '/settings/:section', element: <SettingsPage />, layout: 'full-bleed' },
  { path: '/settings', element: <SettingsPage />, layout: 'full-bleed' },

  // Constrained routes
  { path: '/conversations', element: <Conversations />, layout: 'full-bleed' },
  { path: '/my-data/wellness', element: <WellnessDashboard />, layout: 'constrained' },
  { path: '/agenda', element: <AgendaPage />, layout: 'full-bleed' },
  { path: '/household', element: <HouseholdPage />, layout: 'full-bleed' },
  {
    path: '/financial',
    element: <Navigate to="/financial/overview" replace />,
    layout: 'full-bleed',
  },
  { path: '/financial/overview', element: <OverviewPage />, layout: 'full-bleed' },
  { path: '/financial/budget', element: <BudgetPage />, layout: 'full-bleed' },
  {
    path: '/financial/budget/category/:id',
    element: <BudgetCategoryPage />,
    layout: 'full-bleed',
  },
  { path: '/financial/cash-flow', element: <CashFlowPage />, layout: 'full-bleed' },
  { path: '/financial/retirement', element: <RetirementPage />, layout: 'full-bleed' },
  { path: '/financial/debt-savings', element: <DebtSavingsPage />, layout: 'full-bleed' },
  { path: '/financial/transactions', element: <TransactionsPage />, layout: 'full-bleed' },
  { path: '/financial/investments', element: <InvestmentsPage />, layout: 'full-bleed' },
  { path: '/financial/jobs', element: <JobsTab mode="list" />, layout: 'full-bleed' },
  { path: '/financial/jobs/compare', element: <JobsTab mode="compare" />, layout: 'full-bleed' },
  { path: '/financial/jobs/:id', element: <JobsTab mode="detail" />, layout: 'full-bleed' },
  { path: '/financial/reports', element: <ReportsPage />, layout: 'full-bleed' },
  {
    path: '/financial/reports/spending-analysis',
    element: <SpendingAnalysisPage />,
    layout: 'full-bleed',
  },
  {
    path: '/financial/reports/spending-by-tag',
    element: <SpendingByTagPage />,
    layout: 'full-bleed',
  },
  {
    path: '/financial/reports/:cadence/:id',
    element: <ReportView />,
    layout: 'full-bleed',
  },
  { path: '/quotes', element: <Quotes />, layout: 'constrained' },
  { path: '/areas', element: <AllAreasPage />, layout: 'constrained' },
  // AppRouter renders /search under FullBleedLayout, not the constrained
  // block this route sits near — layout below matches AppRouter, not position.
  { path: '/search', element: <SearchPage />, layout: 'full-bleed' },
  { path: '/research', element: <ResearchPage />, layout: 'full-bleed' },
  { path: '/research/:id', element: <ResearchPage />, layout: 'full-bleed' },
  {
    path: '/research/:collectionId/sources/:sourceId',
    element: <SourceReaderPage />,
    layout: 'full-bleed',
  },
  // The PWA install URLs. Generated from the same constant AppRouter maps over,
  // so a new installable app cannot reach one table and miss the other. A tab
  // never *holds* one of these — they redirect on arrival — but the primary
  // pane resolves against this table too, so an install URL that is absent here
  // would land on "not available" instead of the app's home.
  ...APP_INSTALL_REDIRECTS.map(({ path, to }) => ({
    path,
    element: <Navigate to={to} replace />,
    layout: 'full-bleed' as const,
  })),
];
